/**
 * Çalışma süresi hesabı — kayıtlardan toplam mesai dakikası.
 *
 * Açık aralık (çıkışsız giriş) için millisaniye hassasiyetinde kesirli dakika döner.
 * Böylece sayfa yenilemede canlı sayaçla uyumsuzluk olmaz.
 *
 * MESAİ PENCERESİ (KARAR #11/#13):
 *  - Ödenen süre vardiya [başlangıç, bitiş] penceresiyle sınırlıdır:
 *      · vardiya başlangıcından ÖNCE (erken giriş) → ödenmez
 *      · vardiya bitişinden SONRA (geç çıkış)      → ödenmez
 *  - Vardiya yoksa pencere = [giriş, giriş + 10 saat] (erken-giriş kelepçesi uygulanamaz).
 *  - YETKİLİ personelde pencere dışı kısım, extra ONAYLANMIŞSA ödenir
 *    (onaylı talebin girisKayitId'si seti ile gelir); onaysız → pencerede durur.
 *  Pencere dışı ödenmeyen süre "fazla mesai" olarak ayrılır (personele gizli).
 */

import type { Kayit } from '@/lib/services/kayit-service'
import type { Vardiya } from '@/lib/services/vardiya-service'

export interface MesaiAraligi {
  giris: Kayit
  cikis: Kayit | null
  dakika: number
  acik: boolean
}

export function mesaiAraliklariniHesapla(kayitlar: Kayit[]): MesaiAraligi[] {
  const sirali = [...kayitlar].sort(
    (a, b) => a.zaman.toMillis() - b.zaman.toMillis()
  )
  const araliklar: MesaiAraligi[] = []
  let acikGiris: Kayit | null = null

  for (const k of sirali) {
    if (k.tip === 'giris') {
      if (acikGiris) {
        araliklar.push({ giris: acikGiris, cikis: null, dakika: 0, acik: false })
      }
      acikGiris = k
    } else {
      if (acikGiris) {
        // Kapalı aralık — geçmişte tamamlanmış, tam sayı dakika yeterli
        const dk = Math.floor(
          (k.zaman.toMillis() - acikGiris.zaman.toMillis()) / 60000
        )
        araliklar.push({ giris: acikGiris, cikis: k, dakika: dk, acik: false })
        acikGiris = null
      }
    }
  }

  // Açık aralık — kesirli (millisaniye hassasiyetinde) dakika
  if (acikGiris) {
    const ms = Date.now() - acikGiris.zaman.toMillis()
    const dk = ms / 60000  // float, yuvarlama yok
    araliklar.push({
      giris: acikGiris,
      cikis: null,
      dakika: Math.max(0, dk),
      acik: true,
    })
  }

  return araliklar
}

/**
 * Toplam çalışılan dakika (açık aralık için kesirli) — TAVAN YOK.
 * Geriye uyumluluk için korunur (tatil/doğum günü gün haritası bunu kullanır).
 */
export function toplamCalismaDakika(kayitlar: Kayit[]): number {
  return mesaiAraliklariniHesapla(kayitlar)
    .reduce((acc, m) => acc + m.dakika, 0)
}

export function toplamCalismaSaat(kayitlar: Kayit[]): number {
  return toplamCalismaDakika(kayitlar) / 60
}

// ═══════════════════════════════════════════════════════════════
// MESAİ TAVANI (KARAR #11/#12)
// ═══════════════════════════════════════════════════════════════

const VARDIYASIZ_TAVAN_DK = 600 // 10 saat
const VARDIYA_ESLESME_PENCERESI_MS = 6 * 60 * 60 * 1000 // giriş bu kadar uzaksa eşleme yok

/** "HH:MM" + tarih → yerel epoch ms */
function saatMs(tarih: string, saat: string): number {
  const [y, mo, d] = tarih.split('-').map(Number)
  const [h, mi] = saat.split(':').map(Number)
  return new Date(y, mo - 1, d, h, mi, 0, 0).getTime()
}

function vardiyaBaslangicMs(v: Vardiya): number {
  return saatMs(v.tarih, v.baslangic)
}

/** Vardiya bitişi (gece vardiyası ertesi güne sarkar) */
function vardiyaBitisMs(v: Vardiya): number {
  const bas = saatMs(v.tarih, v.baslangic)
  let bitis = saatMs(v.tarih, v.bitis)
  if (bitis <= bas) bitis += 24 * 60 * 60 * 1000
  return bitis
}

/** Giriş zamanına en uygun planlı vardiyayı bul (yoksa null) */
function girisVardiyasi(girisMs: number, vardiyalar: Vardiya[]): Vardiya | null {
  let best: Vardiya | null = null
  let bestFark = Infinity
  for (const v of vardiyalar) {
    if (v.durum !== 'planli') continue
    const fark = Math.abs(girisMs - vardiyaBaslangicMs(v))
    if (fark < bestFark) {
      bestFark = fark
      best = v
    }
  }
  if (best && bestFark > VARDIYA_ESLESME_PENCERESI_MS) return null
  return best
}

/** Bir aralık girişinin ödeme penceresi (epoch ms). Vardiya eşleşirse
 *  [vardiya başlangıcı, vardiya bitişi]; eşleşmezse [giriş, giriş+10sa]. */
function aralikSinir(girisMs: number, vardiyalar: Vardiya[]): { taban: number; tavan: number } {
  const v = girisVardiyasi(girisMs, vardiyalar)
  if (v) {
    return { taban: vardiyaBaslangicMs(v), tavan: vardiyaBitisMs(v) }
  }
  // Vardiya yok → erken-giriş kelepçesi için referans yok; taban = giriş.
  return { taban: girisMs, tavan: girisMs + VARDIYASIZ_TAVAN_DK * 60000 }
}

export interface TavanliMesai {
  /** Tavana kadar olan, her zaman ödenen dakika (canlı normal sayaç) */
  normalDakika: number
  /** Yetkili personelin tavanı aşan, ÖDENEN ek mesai dakikası (yetkisizde 0) */
  ekMesaiDakika: number
  /** Yetkisiz personelin tavanı aşan, ÖDENMEYEN dakikası — personele gizli (yetkilide 0) */
  odenmeyenFazlaDakika: number
  /** Yetkisiz + açık aralık tavanı aştı → canlı sayaç dursun */
  acikAralikTavanAsti: boolean
  /** Yetkili + açık aralık tavanı aştı → canlı sayaç EK MESAİYE aksın */
  acikAralikEkMesaide: boolean
}

/**
 * Tavan uygulanmış mesai hesabı (KARAR #13 — onaylı + süreli extra modeli).
 * Her giriş-çıkış aralığı, vardiya penceresi [başlangıç, bitiş] ile sınırlanır.
 *
 * Pencere dışı kısım (erken giriş + geç çıkış):
 *   - YETKİSİZ personel → "ödenmeyen fazla" — ödenmez (cron oto-çıkış yazar)
 *   - YETKİLİ personel:
 *       · aralığın girişId'si `onayliExtraMap` içindeyse → vardiya bitişinden
 *         sonra EN FAZLA o kadar dakika "ek mesai" ÖDENİR; aşılırsa ödenmez
 *         (cron onaylanan süre dolunca oto-çıkış yazar).
 *       · değilse (onay bekliyor/yok) → ödenmez.
 *
 * @param extraYetkili    personel.extraMesaiYetkisi
 * @param onayliExtraMap  onaylı extra: girisKayitId → onaylanan ek dakika
 */
export function tavanliMesaiHesabi(
  kayitlar: Kayit[],
  vardiyalar: Vardiya[],
  extraYetkili = false,
  onayliExtraMap: Map<string, number> = new Map()
): TavanliMesai {
  const araliklar = mesaiAraliklariniHesapla(kayitlar)
  let normal = 0
  let ek = 0
  let odenmeyenFazla = 0
  let acikAralikTavanAsti = false
  let acikAralikEkMesaide = false

  for (const a of araliklar) {
    const girisMs = a.giris.zaman.toMillis()
    const cikisMs = a.cikis ? a.cikis.zaman.toMillis() : Date.now()
    const { taban, tavan } = aralikSinir(girisMs, vardiyalar)

    // Ödenen normal süre = aralığın vardiya penceresi [taban, tavan] ile kesişimi.
    const odenenBas = Math.max(girisMs, taban)
    const odenenBit = Math.min(cikisMs, tavan)
    const pencereMs = Math.max(0, odenenBit - odenenBas)
    const pencereDk = a.acik ? pencereMs / 60000 : Math.floor(pencereMs / 60000)

    // Pencere dışında kalan süre (erken giriş + geç çıkış)
    const asanKisim = Math.max(0, a.dakika - pencereDk)
    normal += pencereDk

    if (asanKisim <= 0) continue

    const onayliEk = extraYetkili ? onayliExtraMap.get(a.giris.id) : undefined

    if (onayliEk !== undefined) {
      // Onaylı extra — vardiya bitişinden sonra en fazla onayliEk dakika ödenir
      const odenenEk = Math.min(asanKisim, onayliEk)
      ek += odenenEk
      const fazlasi = asanKisim - odenenEk
      if (fazlasi > 0) odenmeyenFazla += fazlasi  // onaylanan süreyi aştı (çıkış unutuldu)

      if (a.acik) {
        const ekTavanMs = tavan + onayliEk * 60000
        if (cikisMs > ekTavanMs) {
          acikAralikTavanAsti = true     // onaylanan süre doldu → canlı sayaç dursun
        } else if (cikisMs > tavan) {
          acikAralikEkMesaide = true     // ek mesai akıyor (onaylanan süre içinde)
        }
      }
    } else {
      // Yetkisiz, ya da yetkili ama onaysız/bekliyor → ödenmez
      odenmeyenFazla += asanKisim
      if (a.acik && cikisMs > tavan) acikAralikTavanAsti = true
    }
  }

  return {
    normalDakika: normal,
    ekMesaiDakika: ek,
    odenmeyenFazlaDakika: odenmeyenFazla,
    acikAralikTavanAsti,
    acikAralikEkMesaide,
  }
}
